/**
 * NavigationContext - Global navigation state management
 * 
 * Provides:
 * - Navigation history stack for in-app back navigation
 * - Browser history API integration (pushState/popstate)
 * - State preservation (search queries, scroll position)
 * - Session storage persistence for refresh survival
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '../utils/logger';
import { useLocation, useNavigate } from 'react-router-dom';

// Navigation state for a single history entry
export interface NavigationState {
  id: string;                    // Unique ID for this state
  path: string;                  // URL path
  title?: string;                // Display title (e.g., "Document Control")
  panelType?: string;            // Panel type if in TipTap (doc-control, reference-library, etc.)
  searchQuery?: string;          // Search query to restore
  scrollPosition?: number;       // Scroll position to restore
  extras?: Record<string, unknown>;  // Additional state (expanded groups, filters, etc.)
  timestamp: number;             // When this state was created
}

// Context value interface
interface NavigationContextValue {
  // State
  canGoBack: boolean;
  previousState: NavigationState | null;
  currentState: NavigationState | null;
  historyStack: NavigationState[];
  
  // Actions
  goBack: () => void;
  pushState: (state: Omit<NavigationState, 'id' | 'timestamp'>) => void;
  replaceState: (state: Partial<NavigationState>) => void;
  clearHistory: () => void;
  
  // Helpers
  getPreviousTitle: () => string;
}

const NavigationContext = createContext<NavigationContextValue | null>(null);

const STORAGE_KEY = 'app_nav_history';
const MAX_HISTORY = 20;

// Generate unique ID
const generateId = () => `nav_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Get friendly title for a path
const getDefaultTitle = (path: string): string => {
  if (path.startsWith('/workspace')) return 'Aerotage';
  if (path.startsWith('/projects/')) return 'Project Details';
  if (path.includes('/documents/')) return 'Document';
  if (path === '/projects') return 'Projects';
  if (path === '/reference') return 'Reference Library';
  if (path === '/document-control') return 'Document Control';
  if (path === '/company-documents') return 'Company Documents';
  if (path === '/quality') return 'Quality';
  if (path === '/validation') return 'Validation';
  if (path === '/users') return 'Users';
  return 'Previous Page';
};

export function NavigationProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [historyStack, setHistoryStack] = useState<NavigationState[]>([]);
  const isPopStateRef = useRef(false);
  const initializedRef = useRef(false);
  const historyStackRef = useRef<NavigationState[]>([]); // Ref to avoid stale closures
  const lastReplaceRef = useRef<string>(''); // Prevent duplicate replaceState calls
  
  // Keep ref in sync with state
  historyStackRef.current = historyStack;
  
  // Load history from sessionStorage on mount
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Filter out stale entries (older than 1 hour)
        const oneHourAgo = Date.now() - 60 * 60 * 1000;
        const fresh = parsed.filter((s: NavigationState) => s.timestamp > oneHourAgo);
        setHistoryStack(fresh);
      }
    } catch (e) {
      logger.error('[NavigationContext] Failed to load history:', e);
    }
  }, []);
  
  // Save history to sessionStorage when it changes
  useEffect(() => {
    if (!initializedRef.current) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(historyStack));
    } catch (e) {
      logger.error('[NavigationContext] Failed to save history:', e);
    }
  }, [historyStack]);
  
  // Handle browser popstate (back/forward buttons)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // Mark that this navigation was from popstate
      isPopStateRef.current = true;
      
      // If we have state info, try to restore
      if (event.state?.navId) {
        const targetIndex = historyStackRef.current.findIndex(s => s.id === event.state.navId);
        if (targetIndex >= 0) {
          // Pop everything after this index
          setHistoryStack(prev => prev.slice(0, targetIndex + 1));
        }
      } else {
        // Browser went to a page not in our stack, pop the current
        setHistoryStack(prev => prev.slice(0, -1));
      }
      
      // Reset the flag after a tick
      setTimeout(() => {
        isPopStateRef.current = false;
      }, 100);
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []); // Empty deps - uses ref for historyStack
  
  // Track location changes (but not from popstate)
  useEffect(() => {
    if (isPopStateRef.current) {
      // This change was from browser back, don't add to stack
      return;
    }
    
    // Auto-track route changes with basic state
    // Components can call replaceState to add more details
    const currentPath = location.pathname + location.search;
    const lastState = historyStackRef.current[historyStackRef.current.length - 1];
    
    // Don't duplicate if same path
    if (lastState?.path === currentPath) {
      return;
    }
    
    // Don't track login or auth pages
    if (currentPath.startsWith('/login') || currentPath.startsWith('/auth')) {
      return;
    }
    
    // Auto-add basic state for route changes
    const newState: NavigationState = {
      id: generateId(),
      path: currentPath,
      title: getDefaultTitle(currentPath),
      timestamp: Date.now(),
    };
    
    setHistoryStack(prev => {
      const updated = [...prev, newState].slice(-MAX_HISTORY);
      
      // Push to browser history
      try {
        window.history.replaceState({ navId: newState.id }, '', currentPath);
      } catch (_e) {
        // Ignore if we can't modify history
      }
      
      return updated;
    });
  }, [location.pathname, location.search]);
  
  // Push a new navigation state
  const pushState = useCallback((state: Omit<NavigationState, 'id' | 'timestamp'>) => {
    const newState: NavigationState = {
      ...state,
      id: generateId(),
      timestamp: Date.now(),
    };
    
    setHistoryStack(prev => {
      const updated = [...prev, newState].slice(-MAX_HISTORY);
      
      // Push to browser history
      try {
        window.history.pushState({ navId: newState.id }, '', state.path);
      } catch (_e) {
        // Ignore if we can't modify history
      }
      
      return updated;
    });
  }, []);
  
  // Replace current state (update details without adding new entry)
  const replaceState = useCallback((updates: Partial<NavigationState>) => {
    // Create a key to detect duplicate calls with same data
    const updateKey = JSON.stringify(updates);
    if (updateKey === lastReplaceRef.current) {
      return; // Skip duplicate update
    }
    lastReplaceRef.current = updateKey;
    
    setHistoryStack(prev => {
      if (prev.length === 0) return prev;
      
      const current = prev[prev.length - 1];
      
      // Check if anything actually changed
      const hasChanges = Object.keys(updates).some(key => {
        const k = key as keyof NavigationState;
        return updates[k] !== current[k];
      });
      
      if (!hasChanges) {
        return prev; // No changes, return same reference
      }
      
      const updated = [...prev];
      updated[updated.length - 1] = {
        ...current,
        ...updates,
        id: current.id, // Keep same ID
        timestamp: current.timestamp, // Keep original timestamp
      };
      
      // Update browser history state
      try {
        window.history.replaceState(
          { navId: current.id },
          '',
          updates.path || current.path
        );
      } catch (_e) {
        // Ignore if we can't modify history
      }
      
      return updated;
    });
  }, []);
  
  // Go back to previous state
  const goBack = useCallback(() => {
    if (historyStack.length <= 1) {
      // No history, go to default
      navigate('/workspace');
      return;
    }
    
    // Get the state to return to
    const previousState = historyStack[historyStack.length - 2];
    
    // Pop current state
    setHistoryStack(prev => prev.slice(0, -1));
    
    // Navigate to previous path
    if (previousState) {
      // Use browser back if possible for better UX
      window.history.back();
    } else {
      navigate('/workspace');
    }
  }, [historyStack, navigate]);
  
  // Clear all history
  const clearHistory = useCallback(() => {
    setHistoryStack([]);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);
  
  // Get previous state title
  const getPreviousTitle = useCallback((): string => {
    if (historyStack.length <= 1) return 'Back';
    const prev = historyStack[historyStack.length - 2];
    return prev?.title || getDefaultTitle(prev?.path || '');
  }, [historyStack]);
  
  // Computed values
  const canGoBack = historyStack.length > 1;
  const previousState = historyStack.length > 1 ? historyStack[historyStack.length - 2] : null;
  const currentState = historyStack.length > 0 ? historyStack[historyStack.length - 1] : null;
  
  const value: NavigationContextValue = {
    canGoBack,
    previousState,
    currentState,
    historyStack,
    goBack,
    pushState,
    replaceState,
    clearHistory,
    getPreviousTitle,
  };
  
  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

// Hook to use navigation context
// eslint-disable-next-line react-refresh/only-export-components
export function useNavigation(): NavigationContextValue {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}

// Hook to use navigation with null safety (for components that might be outside provider)
// eslint-disable-next-line react-refresh/only-export-components
export function useNavigationSafe(): NavigationContextValue | null {
  return useContext(NavigationContext);
}

export default NavigationContext;
